const CALC_PREFIX = "calc:";

const cleanId = (value) =>
  String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9_]/g, "")
    .slice(0, 120);

const parseNumericValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  if (value && typeof value === "object") {
    const nested =
      value.value ??
      value.amount ??
      value.number ??
      value.total ??
      value.label ??
      value.name;
    return parseNumericValue(nested);
  }
  let raw = String(value ?? "").trim();
  if (!raw) return NaN;
  raw = raw.replace(/[^0-9,.+\-]/g, "");
  if (!raw) return NaN;

  const comma = raw.lastIndexOf(",");
  const dot = raw.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) {
    if (comma > dot) raw = raw.replace(/\./g, "").replace(",", ".");
    else raw = raw.replace(/,/g, "");
  } else if (comma >= 0) {
    raw = raw.replace(/\./g, "").replace(",", ".");
  } else {
    const dotCount = (raw.match(/\./g) || []).length;
    if (dotCount > 1) {
      const last = raw.lastIndexOf(".");
      raw = raw.slice(0, last).replace(/\./g, "") + raw.slice(last);
    }
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const buildDocumentFormulaToken = ({
  variableId,
  operator,
  operandType,
  operand,
}) => {
  const first = cleanId(variableId);
  if (!first) throw new Error("Selecione uma variável para a função.");
  const safeOperator = ["+", "-", "*", "/"].includes(operator) ? operator : "/";
  if (operandType === "variable") {
    const second = cleanId(operand);
    if (!second) throw new Error("Selecione a segunda variável.");
    return `{{${CALC_PREFIX}${first}|${safeOperator}|var:${second}}}`;
  }
  const numeric = parseNumericValue(operand);
  if (!Number.isFinite(numeric)) throw new Error("Informe um número válido.");
  return `{{${CALC_PREFIX}${first}|${safeOperator}|number:${numeric}}}`;
};

export const parseDocumentFormula = (expression) => {
  const raw = String(expression || "").trim();
  if (!raw.startsWith(CALC_PREFIX)) return null;
  const body = raw.slice(CALC_PREFIX.length);
  const parts = body.split("|");
  if (parts.length !== 3) return null;
  const variableId = cleanId(parts[0]);
  const operator = parts[1];
  const operandRaw = parts[2];
  if (!variableId || !["+", "-", "*", "/"].includes(operator)) return null;

  if (operandRaw.startsWith("var:")) {
    const operand = cleanId(operandRaw.slice(4));
    if (!operand) return null;
    return { variableId, operator, operandType: "variable", operand };
  }
  if (operandRaw.startsWith("number:")) {
    const operand = parseNumericValue(operandRaw.slice(7));
    if (!Number.isFinite(operand)) return null;
    return { variableId, operator, operandType: "number", operand };
  }
  return null;
};

export const evaluateDocumentFormula = (
  expression,
  values = {},
  resolveValue = null,
) => {
  const formula = parseDocumentFormula(expression);
  if (!formula) return null;
  const readValue = (id) =>
    typeof resolveValue === "function" ? resolveValue(id) : values?.[id];
  const left = parseNumericValue(readValue(formula.variableId));
  if (!Number.isFinite(left)) return "";
  const right =
    formula.operandType === "variable"
      ? parseNumericValue(readValue(formula.operand))
      : formula.operand;
  if (!Number.isFinite(right)) return "";

  let result;
  if (formula.operator === "+") result = left + right;
  else if (formula.operator === "-") result = left - right;
  else if (formula.operator === "*") result = left * right;
  else {
    if (right === 0) return "";
    result = left / right;
  }
  if (!Number.isFinite(result)) return "";
  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 4,
  }).format(result);
};

export const describeDocumentFormula = (expression, fields = []) => {
  const formula = parseDocumentFormula(expression);
  if (!formula) return "";
  const map = new Map(
    (Array.isArray(fields) ? fields : []).map((field) => [
      String(field?.id || ""),
      String(field?.label || field?.id || ""),
    ]),
  );
  const op = { "+": "+", "-": "−", "*": "×", "/": "÷" }[formula.operator];
  const right =
    formula.operandType === "variable"
      ? map.get(formula.operand) || formula.operand
      : String(formula.operand).replace(".", ",");
  return `${map.get(formula.variableId) || formula.variableId} ${op} ${right}`;
};

const normalizeFormulaVariableKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const resolveDocumentFormulaValue = (id, values = {}, fields = []) => {
  const raw = String(id || "").trim();
  if (!raw) return undefined;
  if (Object.prototype.hasOwnProperty.call(values || {}, raw)) return values[raw];

  const normalized = normalizeFormulaVariableKey(raw);
  if (normalized && Object.prototype.hasOwnProperty.call(values || {}, normalized)) {
    return values[normalized];
  }

  const candidates = (Array.isArray(fields) ? fields : []).filter((field) => {
    const fieldId = String(field?.id || "");
    const label = String(field?.label || "");
    return (
      normalizeFormulaVariableKey(fieldId) === normalized ||
      normalizeFormulaVariableKey(label) === normalized
    );
  });
  if (candidates.length === 1) {
    const fieldId = String(candidates[0]?.id || "");
    if (Object.prototype.hasOwnProperty.call(values || {}, fieldId)) return values[fieldId];
    const normalizedFieldId = normalizeFormulaVariableKey(fieldId);
    if (Object.prototype.hasOwnProperty.call(values || {}, normalizedFieldId)) {
      return values[normalizedFieldId];
    }
  }

  return undefined;
};

export const renderDocumentFormulaTokens = (html, values = {}, fields = []) =>
  String(html || "").replace(/{{\s*(calc:[^}]+)\s*}}/gi, (_match, expression) => {
    const rendered = evaluateDocumentFormula(
      expression,
      values,
      (id) => resolveDocumentFormulaValue(id, values, fields),
    );
    return rendered === null || rendered === undefined ? "" : String(rendered);
  });

export const DOCUMENT_FORMULA_PREFIX = CALC_PREFIX;
